/**
 * PremiumPaymentGateway
 *
 * Handles the payment checkout flow as a modal
 * Handles activation error
 *
 * If the selected product type is free then this will auto activate the listing.
 * If the selected product type is premium then the payment modal will launch.
 * If the selected product type is premium and within the expiration date then this will auto activate the listing.
 */
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isPast } from 'date-fns';

import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyActivateWebApiHandler_activate } from '@zg-rentals/rental-manager-env';

import useAccessActivateOnReview from '../../../../hooks/useAccessActivateOnReview';

import { PaymentGatewayContext } from '../../context';
import { RMWContext } from '../../../../providers/RMWContext';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import PayAndPublishCSEvents from '../../../../model/ClickstreamEvent/PayAndPublishCSEvents';
import PostActivationModalCSEvents from '../../../../model/ClickstreamEvent/PostActivationModalCSEvents';

import FreemiumErrorModal from '../../../Modal/FreemiumErrorModal';
import PaymentModal from '../../../Modal/PaymentModal';
import PremiumMidfunnelSuccessModal from '../../../Modal/PremiumMidfunnelSuccessModal';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';

import DispatchConstants from '../../../../constants/DispatchConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';
import { RMW_SUB_PATH } from '../../../../pathsManifest';
import { ACTIVATE_LISTING_MUTATION_KEY } from '../../../../constants/QueryConstants';

import { setQueryFreemiumActiveProduct } from '../../../../hooks/queries/useGetPropertyDetailsOverview';
import { trackGtmEvent } from '../../../../../client/analytics';
import { ZRM_GTM_EVENTS } from '../../../../../client/analytics/gtmEvents';
import ModularListingCSEvents from '../../../../model/ClickstreamEvent/ModularListingCSEvents';

const propTypes = {
  displayPaymentGateway: PropTypes.bool,
  updateDisplayPaymentGateway: PropTypes.func,
  onFreemiumResponse: PropTypes.func,
};

const defaultProps = {
  displayPaymentGateway: false,
  updateDisplayPaymentGateway: () => {},
  onFreemiumResponse: () => {},
};

const MODAL_SWAP_DELAY = 100;

const PremiumPaymentGateway = ({
  // use value from prop until all PaymentGateway instances are launched from App
  // if not launched from App, displayPaymentGateway in pgState will be out of sync
  displayPaymentGateway,
  updateDisplayPaymentGateway,
  onFreemiumResponse,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    pgState: { displayContext, isPremiumMidfunnelPayment },
  } = useContext(PaymentGatewayContext);

  const { paths } = useContext(RMWContext);

  const { reducerDispatch: mlReducerDispatch } = useContext(ModularListingContext);

  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [publishError, setPublishError] = useState({ display: false });
  const [displayMidfunnelSuccessModal, setDisplayMidfunnelSuccessModal] = useState(false);

  const { canAccessActivateOnReview } = useAccessActivateOnReview();
  const { mutate: activate } = useRentalManagerUserPropertyActivateWebApiHandler_activate({
    mutationKey: ACTIVATE_LISTING_MUTATION_KEY,
  });

  const {
    active,
    listingAlias,
    freemium,
    freemium: {
      activeProduct,
      activeProduct: { type: activeProductType, expirationTimestamp: freemiumActiveProductTimestamp },
      products,
    },
    selectedProductType,
  } = currentProperty;

  const isActiveProductExpired = isPast(new Date(freemiumActiveProductTimestamp));
  const isActiveProductPremiumAndNotExpired =
    activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;
  const foundIndex = products.findIndex((product) => product.type === selectedProductType);
  const selectedProduct = products[foundIndex];
  const { type } = selectedProduct;

  useEffect(() => {
    if (displayPaymentGateway) {
      if (shouldAutoActivate() && !isPremiumMidfunnelPayment) {
        activateListing();
        return;
      }

      if (isActiveProductPremiumAndNotExpired) {
        return;
      }

      setDisplayPaymentModal(true);
    } else {
      setDisplayPaymentModal(false);
    }
  }, [displayPaymentGateway]);

  const getUpdatedActiveProduct = ({ activateResponse, freemiumChargeResponse }) => {
    // we can not downgrade the active product
    // use active product information is not activating from the charge endpoint
    const tempProduct = freemiumChargeResponse ? selectedProduct : activeProduct;
    const { price, duration, type } = tempProduct;

    const computedExpirationDateTimestamp = freemiumChargeResponse
      ? freemiumChargeResponse?.expirationDateTimestamp
      : activateResponse?.expirationMs;

    const response = {
      ...activeProduct,
      daysExtended: activateResponse?.daysExtended,
      expirationTimestamp: computedExpirationDateTimestamp,
      type,
      price,
      duration,
    };

    return response;
  };

  const shouldAutoActivate = () => {
    return (type === PRODUCT_TYPE_NAME_TO_ID.FREE || isActiveProductPremiumAndNotExpired) && !active;
  };

  // TODO: decouple activation from payment gateway
  // will need to create a post activation analytics util function
  const activateListing = (freemiumChargeResponse = false) => {
    return activate(
      { getParams: { listingAlias } },
      {
        onSuccess: (activateResponse) => {
          const updatedActiveProduct = getUpdatedActiveProduct({ activateResponse, freemiumChargeResponse });
          const productOverviewDataFromCache = queryClient.getQueryData(['getPropertyDetailsOverview', listingAlias]);

          syncWithCurrentProperty({ updatedActiveProduct });
          dispatch(
            PropertiesActions.updatePostActivationState({
              daysExtended: updatedActiveProduct?.daysExtended,
              displayPostActivationModal: true,
            }),
          );
          if (productOverviewDataFromCache) {
            syncWithQueryCache({ updatedActiveProduct });
          }

          const csEventParams = {
            currentProperty: {
              ...currentProperty,
              freemium: {
                ...freemium,
                activeProduct: updatedActiveProduct,
              },
            },
            user,
          };

          PayAndPublishCSEvents.ActivateListing(csEventParams);

          if (freemiumChargeResponse) {
            // handlePremiumActivation csEvents
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PAY_PUBLISH });

            PostActivationModalCSEvents.CompletesPremiumUpsell(csEventParams);
          } else {
            // handleFreeActivation csEvents
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });

            // always update display
            updateDisplayPaymentGateway({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          }

          onFreemiumResponse({
            expirationDateTimestamp: activateResponse?.expirationMs,
            activeProductType: updatedActiveProduct?.type,
          });

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          handleActivationError(error);
        },
      },
    );
  };

  const syncWithCurrentProperty = ({ updatedActiveProduct }) => {
    const { daysExtended, expirationTimestamp } = updatedActiveProduct;
    dispatch(
      PropertiesActions.updateCurrentProperty({
        ...currentProperty,
        active: isPremiumMidfunnelPayment ? false : true,
        daysExtended,
        expirationDateTimestamp: expirationTimestamp,
        freemium: {
          ...freemium,
          activeProduct: updatedActiveProduct,
        },
      }),
    );
  };

  const syncWithQueryCache = ({ updatedActiveProduct }) => {
    setQueryFreemiumActiveProduct({
      config: {
        queryClient,
        listingAlias,
      },
      payload: {
        activeProduct: updatedActiveProduct,
      },
    });
  };

  const handleActivationError = (error) => {
    setPublishError({
      display: true,
      error,
      onClose: () => {
        updateDisplayPaymentGateway({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
        setPublishError({ display: false });
      },
    });
  };

  const handleFreemiumChargeSuccess = (response) => {
    if (response.isPaymentValid) {
      if (!isPremiumMidfunnelPayment && !active) {
        activateListing(response);

        ModularListingCSEvents.CompleteUpgradeToPremium({
          currentProperty,
          user,
          triggerSource: ModularListingCSEvents.TRIGGER_SOURCES.AT_ACTIVATION,
        });
      } else {
        if (canAccessActivateOnReview && !isPremiumMidfunnelPayment) {
          actions.updateWizardComplete({ reducerDispatch: mlReducerDispatch, isWizardComplete: true });
          window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`);
        } else {
          setDisplayMidfunnelSuccessModal(true);
        }
        setDisplayPaymentModal(false);

        const updatedActiveProduct = getUpdatedActiveProduct({
          activateResponse: {},
          freemiumChargeResponse: response,
        });

        const productOverviewDataFromCache = queryClient.getQueryData(['getPropertyDetailsOverview', listingAlias]);
        syncWithCurrentProperty({ updatedActiveProduct });
        if (productOverviewDataFromCache) {
          syncWithQueryCache({ updatedActiveProduct });
        }

        PostActivationModalCSEvents.CompletesPremiumUpsell({
          currentProperty: {
            ...currentProperty,
            freemium: {
              ...freemium,
              activeProduct: updatedActiveProduct,
            },
          },
          user,
        });

        if (active === false) {
          ModularListingCSEvents.CompleteUpgradeToPremium({
            currentProperty,
            user,
            triggerSource: ModularListingCSEvents.TRIGGER_SOURCES.PREACTIVATION,
          });
        }
      }

      // always update display
      updateDisplayPaymentGateway({ displayPaymentGateway: false, displayContext });
      dispatch({
        type: DispatchConstants.RESET_CREDIT_CARD_CAPTURE_POST_PAYMENT,
      });
    }

    onFreemiumResponse(response);
  };

  const updateSelectedProductType = (selectedProductType) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handleFreemiumErrorPublishForFreeClick = () => {
    setPublishError({ display: false });
    updateSelectedProductType(PRODUCT_TYPE_NAME_TO_ID.FREE);
    activateListing();
  };

  const handleFreemiumErrorTryAgainClick = () => {
    setPublishError({ display: false });

    setTimeout(() => {
      updateDisplayPaymentGateway({ displayPaymentGateway: true, isPremiumMidfunnelPayment });
    }, MODAL_SWAP_DELAY);
  };

  const handleFreemiumChargeError = (err) => {
    updateDisplayPaymentGateway({ displayPaymentGateway: false, isPremiumMidfunnelPayment });

    setTimeout(() => {
      setPublishError({
        display: true,
        error: err,
        onClose: () => {
          updateDisplayPaymentGateway({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          setPublishError({ display: false });
        },
      });
    }, MODAL_SWAP_DELAY);
  };

  return (
    <div data-testid="PremiumPaymentGateway">
      {publishError.display ? (
        <FreemiumErrorModal
          error={publishError.error}
          onClose={publishError.onClose}
          onTryAgainClick={handleFreemiumErrorTryAgainClick}
          onPublishForFreeClick={handleFreemiumErrorPublishForFreeClick}
        />
      ) : null}
      {displayPaymentModal && (
        <PaymentModal
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={handleFreemiumChargeError}
          listing={currentProperty}
          listingAlias={listingAlias}
          isRenderedInPageView={false}
          onClose={() => {
            // always update display
            setDisplayPaymentModal(false);
            updateDisplayPaymentGateway({ displayPaymentGateway: false, isPremiumMidfunnelPayment: false });
          }}
        />
      )}
      {displayMidfunnelSuccessModal && (
        <PremiumMidfunnelSuccessModal onClose={() => setDisplayMidfunnelSuccessModal(false)} isActive={active} />
      )}
    </div>
  );
};

PremiumPaymentGateway.propTypes = propTypes;
PremiumPaymentGateway.defaultProps = defaultProps;

export default PremiumPaymentGateway;
